import React, { useContext, useEffect, useState } from "react";
import { createOrg, enrichOrg, getCompanyInsights, getOrg } from "../../../apis/ext";
import { useMutation } from "react-query";
import CompanyInfoCard from "./CompanyInfoCard";
import NoInfo from "../NoInfo";
import { Numeral } from "react-numeral";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import SharedDataContext from "../../../SharedDataContext";
import Image from "../../Tags/Image";

export default function CompanyInfo({
  companyName = "",
  miniView = true,
  changeMiniView,
  contactLinkedinUrl = "",
  crmObjectsConfig = [],
  getActiveCRMID = null,
}) {
  const captureEvent = useCaptureEvent();
  const [showMoreDes, setShowMoreDes] = useState(true);
  const { companyLinkedinUrl, userData, params } = useContext(SharedDataContext);
  const { activeWorkspace, preferences } = userData || {};
  const [companyData, setCompanyData] = useState({});
  const [hasPendingEnrichmentAction, setHasPendingEnrichmentAction] = useState(false);
  const [hasPendingEnrichmentTechnography, setHasPendingEnrichmentTechnography] = useState(false);

  const extractCompanyId = (url) => {
    if (url) {
      const regex = /^https:\/\/www\.linkedin\.com\/company\/([^\/?]+)[\/?]?.*$/;
      const match = url.match(regex);
      if (match) {
        return match[1];
      }
      return null;
    } else {
      return null;
    }
  };

  const processOrgGetData = (data) => {
    console.log("enrich_org data", data);
    if (data?.status === "Pending" && data?.retry_after) {
      setHasPendingEnrichmentAction(true);
      return;
    }

    const company_data = data?.data?.organization;
    console.log("company_data", company_data);
    setCompanyData(company_data);
    setHasPendingEnrichmentAction(data?.data?.hasPendingEnrichmentAction);
    if (
      data?.data?.organization &&
      data?.data?.organization.technography_enrichment_status &&
      data?.data?.organization.technography_enrichment_status === "Pending"
    ) {
      setHasPendingEnrichmentTechnography(true);
    } else {
      setHasPendingEnrichmentTechnography(false);
    }
  };

  const enrichOrgFn = useMutation("enrich_org", enrichOrg, {
    onSuccess(data, variables, context) {
      processOrgGetData(data);
    },
  });

  const getOrgFn = useMutation("enrich_org", getOrg, {
    onSuccess(data, variables, context) {
      console.log("get org data", data);
      processOrgGetData(data);
    },
  });
  const handleGetOrg = (id) => {
    if (!id) {
      return;
    }
    getOrgFn.mutate({
      id: id,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const createOrgFn = useMutation("create_org", createOrg, {
    onSuccess(data, variables, context) {
      handleGetOrg(data?.data?.organization?.id);
    },
    onError(error) {
      console.log(error);
    },
  });

  const enrichCompanyDetails = (url) => {
    let isDomain = false;
    if (url && !url.includes("https://www.linkedin.com/company/")) {
      isDomain = true;
    }

    const payloadForCreateOrg = {};
    if (isDomain) {
      payloadForCreateOrg.domain = url;
    } else {
      payloadForCreateOrg.linkedin_url = url;
    }

    if (params.source || params.source === "salesforce") {
      if (companyData?.id) {
        handleGetOrg(companyData?.id);
      } else {
        createOrgFn.mutate({
          data: {
            organization: { ...payloadForCreateOrg },
          },
        });
      }
    } else {
      const id = isDomain ? url : extractCompanyId(url);
      console.log("companyId", id);

      enrichOrgFn.mutate({
        id: isDomain ? url : extractCompanyId(url),
        domain: isDomain ? true : false,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      });
    }
  };

  useEffect(() => {
    if (userData && userData.activeWorkspace && userData.activeWorkspace.id && companyLinkedinUrl) {
      console.log("hasPendingEnrichmentAction in polling", hasPendingEnrichmentAction);
      enrichCompanyDetails(companyLinkedinUrl);
      // This function is what you want to run at every interval
      const doPolling = () => {
        if (hasPendingEnrichmentAction) {
          enrichCompanyDetails(companyLinkedinUrl);
        }
      };

      // Start polling
      const intervalId = setInterval(doPolling, 5000); // 5000ms = 5 seconds

      if (!hasPendingEnrichmentAction) {
        clearInterval(intervalId);
      }

      // Cleanup function to clear the interval when the component unmounts or the effect re-runs
      return () => clearInterval(intervalId);
    }
  }, [userData && userData.activeWorkspace, companyLinkedinUrl, hasPendingEnrichmentAction]);

  const addTabParam = (tab) => {
    const params = new URLSearchParams(window.location.search);
    params.set("tab", tab);
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  };

  // get insights

  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insights, setInsights] = useState({});
  const [insightsErr, setInsightsErr] = useState(false);
  const getCompanyInsightsFn = useMutation("get_company_insights", getCompanyInsights, {
    onSuccess(data, variables, context) {
      setInsights(data);
      setInsightsLoading(false);
      setInsightsErr(false);
    },
    onError(err) {
      setInsights({});
      setInsightsLoading(false);
      setInsightsErr(true);
    },
  });
  const handleGetCompanyInsights = (workspaceId, organizationId) => {
    if (insights && Object.keys(insights).length > 0) {
      setInsightsLoading(false);
      return;
    } else {
      setInsightsLoading(true);
    }
    if (insightsErr) {
      return;
    }

    if (workspaceId && organizationId) {
      getCompanyInsightsFn.mutate({
        organization_id: organizationId,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });
    }
  };

  if (
    (!companyData?.name && hasPendingEnrichmentAction) ||
    (enrichOrgFn.isLoading && !hasPendingEnrichmentAction && !companyData?.name)
  ) {
    return (
      <div className="py-6">
        <Image src="/images/ext/company-fetching.svg" className="w-9/12 mx-auto max-w-[300px] no-invert" />
        <div className="flex w-full justify-center items-center mt-6">
          <div className="loader_circle inline-block mr-2"></div>
          <span className="text-base text-gray-900">Fetching company details</span>
        </div>
      </div>
    );
  }
  if (!companyData?.name && !hasPendingEnrichmentAction && enrichOrgFn.isSuccess) {
    return <NoInfo />;
  }
  if (enrichOrgFn.isError) {
    return (
      <div className="border border-border rounded-[14px] overflow-hidden bg-background">
        <div className="flex p-3 items-center border-b border-border ">Something went wrong</div>
        <div className="py-4 px-4 border-b border-border space-y-3">Looks like we got some error.</div>
      </div>
    );
  }

  const ViewDetailCTA = ({ tab = "" }) => (
    <button
      onClick={() => {
        changeMiniView(false);
        addTabParam(tab);
        captureEvent("EXC_Company_ViewDetailsBtn");
      }}
      className="inline-flex items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-background border-brand-300 text-brand-700 px-4 py-2 justify-between h-9 rounded-[8px] font-semibold"
    >
      View Details
    </button>
  );

  const Card = ({ icon, label, value, doller = true }) => (
    <div className="p-6 border border-gray-200 bg-gray-50 rounded-xl flex-1">
      <Image src={icon} className="mb-5 no-invert" />
      <span className="text-[12px] capitalize whitespace-pre mb-2 leading-none block">{label}</span>
      <span className="text-gray-900 font-bold leading-none uppercase text-2xl block">
        {doller ? "$" : ""}

        <Numeral
          className="text-gray-900 font-bold leading-none uppercase "
          value={value}
          format={"0.[0]a"}
        />
      </span>
    </div>
  );

  return (
    <>
      {companyData && companyData.id && (
        <CompanyInfoCard
          contactLinkedinUrl={contactLinkedinUrl}
          company={companyData}
          activeWorkspace={activeWorkspace}
          companyName={companyData ? companyData?.name : companyName}
          hasPendingEnrichmentAction={hasPendingEnrichmentAction}
          activeCrm={
            activeWorkspace?.preferences &&
            activeWorkspace?.preferences?.active_crm_workspace_integration_config_id
          }
          miniView={miniView}
          // insights={{ data: insights, isLoading: insightsLoading }}
          crmObjectsConfig={crmObjectsConfig}
          getActiveCRMID={getActiveCRMID}
        />
      )}
      {miniView && (
        <>
          <div className="border rounded-[14px] overflow-hidden border-border  bg-background relative mt-4">
            <div className="flex pt-4 pb-1 px-4 justify-between items-center ">
              <span className="text-[16px] text-text-700 mr-3 font-bold">Summary</span>
            </div>
            <div className="px-4 mb-4">
              <p className="text-gray-600 text-sm leading-7">
                {`${companyData?.description?.substring(0, 200)}`}
                {200 < companyData?.description?.length ? "..." : ""}
              </p>
            </div>
            <div className="flex pb-1 px-4 justify-between items-center ">
              <span className="text-[16px] text-text-700 mr-3 font-bold">Industry</span>
            </div>
            <div className="px-4 mb-4">
              <p className="text-gray-600 text-sm leading-7">
                {companyData?.industry ? companyData?.industry : "-"}
              </p>
            </div>
            {companyData && companyData.naics_codes && companyData.naics_codes.length > 0 && (
              <>
                <div className="flex pb-1 px-4 justify-between items-center ">
                  <span className="text-[16px] text-text-700 mr-3 font-bold">NAICS Codes</span>
                </div>
                <div className="px-4 mb-4">
                  {companyData.naics_codes.map((item) => (
                    <span className="inline-block text-[12px] bg-background text-text-700 border border-border px-3 py-1 rounded-[8px] mr-3 mb-3">
                      {item?.name ? item?.name : item?.id}
                    </span>
                  ))}
                </div>
              </>
            )}
            {companyData && companyData.sic_codes && companyData.sic_codes.length > 0 && (
              <>
                <div className="flex pb-1 px-4 justify-between items-center ">
                  <span className="text-[16px] text-text-700 mr-3 font-bold">SIC Codes</span>
                </div>
                <div className="px-4 mb-4">
                  {companyData.sic_codes.map((item) => (
                    <span className="inline-block text-[12px] bg-background text-text-700 border border-border px-3 py-1 rounded-[8px] mr-3 mb-3">
                      {item?.name ? item?.name : item?.id}
                    </span>
                  ))}
                </div>
              </>
            )}
            <div className="flex pb-1 px-4 justify-between items-center ">
              <span className="text-[16px] text-text-700 mr-3 font-bold">Founded</span>
            </div>
            <div className="px-4 mb-4">
              <p className="text-gray-600 text-sm leading-7">
                {companyData?.year_founded ? companyData?.year_founded : "-"}
              </p>
            </div>
            <div className="flex pb-1 px-4 justify-between items-center ">
              <span className="text-[16px] text-text-700 mr-3 font-bold">Location</span>
            </div>
            <div className="px-4 mb-4">
              <p className="text-gray-600 text-sm leading-7">
                {companyData?.headquarters?.city ? companyData?.headquarters?.city : "-"}
              </p>
            </div>
            {(!params.source || params.source !== "salesforce") && (
              <div className="px-4 mb-4">
                <ViewDetailCTA tab="overview" />
              </div>
            )}
          </div>
          {(companyData?.revenue?.amount_in_usd ||
            (companyData?.funding?.recent_round && companyData?.funding?.recent_round?.amount_in_usd) ||
            companyData?.funding?.total_funding_amount ||
            companyData?.valuation?.amount_in_usd) && (
            <div className="border rounded-[14px] overflow-hidden border-border  bg-background relative mt-4">
              <div className="flex pt-4 pb-3 px-4 justify-between items-center ">
                <span className="text-[16px] text-text-700 mr-3 font-bold">Financials</span>
              </div>
              <>
                <div className="flex my-4 justify-between space-x-4 px-4">
                  {companyData?.revenue?.amount_in_usd && (
                    <Card
                      value={companyData?.revenue?.amount_in_usd}
                      label="Annual Revenue"
                      icon={"/images/ext/money.svg"}
                    />
                  )}
                  {companyData?.funding?.recent_round &&
                    companyData?.funding?.recent_round?.amount_in_usd && (
                      <Card
                        value={companyData?.funding?.recent_round?.amount_in_usd}
                        label={companyData?.funding?.recent_round?.name}
                        icon={"/images/ext/money.svg"}
                      />
                    )}
                  {companyData?.funding?.total_funding_amount && (
                    <Card
                      value={companyData?.funding?.total_funding_amount}
                      label={"Total funding"}
                      icon={"/images/ext/money.svg"}
                    />
                  )}
                  {companyData?.valuation?.amount_in_usd && (
                    <Card
                      value={companyData?.valuation?.amount_in_usd}
                      label={"Valuation"}
                      icon={"/images/ext/valuation.svg"}
                    />
                  )}
                </div>
              </>

              {(!params.source || params.source !== "salesforce") && (
                <div className="px-4 mb-4 mt-2">
                  <ViewDetailCTA tab="overview" />
                </div>
              )}
            </div>
          )}
          {companyData?.technology_used &&
            companyData?.technology_used &&
            companyData?.technology_used.length > 0 && (
              <div className="border rounded-[14px] overflow-hidden border-border  bg-background relative mt-4">
                <div className="flex pt-4 pb-3 px-4 justify-between items-center ">
                  <span className="text-[16px] text-text-700 mr-3 font-bold">Technology Used</span>
                </div>
                <div className="px-4 mb-4">
                  {companyData?.technology_used &&
                    companyData?.technology_used &&
                    companyData?.technology_used.length > 0 &&
                    companyData?.technology_used
                      .slice(0, 5)
                      .map((item) => (
                        <span className="inline-block text-[12px] bg-background text-gray-blue-700 border border-gray-blue-700 px-2 py-1 rounded-[20px] mr-3 mb-3">
                          {item}
                        </span>
                      ))}
                </div>
                {(!params.source || params.source !== "salesforce") && (
                  <div className="px-4 mb-4">
                    <ViewDetailCTA tab="technologies" />
                  </div>
                )}
              </div>
            )}
          <div className="border rounded-[14px] overflow-hidden border-border  bg-background relative mt-4">
            <div className="flex pt-4 pb-3 px-4 justify-between items-center ">
              <span className="text-[16px] text-text-700 mr-3 font-bold">People</span>
            </div>
            <div className="px-4 mb-4 flex justify-between">
              <div className="flex-1">
                <p className="text-sm   text-text-700 mb-1">Total Employees</p>
                <p className="text-2xl font-normal text-gray-900">
                  {companyData.headcount && companyData.headcount?.total ? (
                    companyData.headcount?.total
                  ) : (
                    <>
                      {companyData?.employee_count?.exact ? (
                        companyData?.employee_count?.exact
                      ) : (
                        <>
                          {companyData?.employee_count?.range?.min}{" "}
                          {companyData?.employee_count?.range?.max &&
                            `- ${companyData?.employee_count?.range?.max}`}
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-sm   text-text-700 mb-1">Monthly growth</p>
                <p className="text-2xl text-gray-900 mt-3">
                  <span className="inline-block">
                    {companyData?.headcount?.mom_absolute ? (
                      <>
                        {companyData?.headcount?.mom_absolute < 0 ? "" : "+"}
                        {companyData?.headcount?.mom_absolute}
                      </>
                    ) : (
                      <span className="text-sm relative -top-3">&nbsp;</span>
                    )}
                  </span>
                  {companyData?.headcount?.mom_percent && (
                    <span className="inline-flex space-x-1 ml-3 relative -top-1.5">
                      <Image
                        className="no-invert"
                        src={
                          companyData?.headcount?.mom_percent < 0
                            ? "/images/ext/companyinfo/downtrend.svg"
                            : "/images/ext/companyinfo/uptrend.svg"
                        }
                      />
                      <span
                        className={`${
                          companyData?.headcount?.mom_percent < 0 ? "text-error-600" : "text-success-500"
                        } text-sm`}
                      >
                        {companyData?.headcount?.mom_percent}%
                      </span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            {(!params.source || params.source !== "salesforce") && (
              <div className="px-4 mb-4">
                <ViewDetailCTA tab="people" />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
